import * as React from 'react';
import px from 'prop-types';
import cx from 'classnames';
import debounce from 'lodash.debounce';
import * as SearchApi from 'Common/features/search/api';
import { Cookie } from 'Common/utils';
import { HeaderController } from 'Common/controllers';
import { useViewport } from 'Common/hooks';
import SearchDrawerLinks from './SearchDrawerLinks';
import { SearchContext } from './SearchProvider';
import SearchBar from './SearchBar';
import HeaderSearchResult from './HeaderSearchResult';
import { Translation } from 'Common/components/localization';
import { ResultsGrid, Section } from 'Common/components/ui';

const initialPageSize = 5;
const textSearchPageSize = 8;

function updateRecents(searches, relatedSearchCount, value) {
    let newSearches = searches?.map((s) => s.title) || [];

    if (newSearches.includes(value)) return;
    newSearches.unshift(value);

    if (newSearches.length > relatedSearchCount) {
        newSearches = newSearches.slice(0, relatedSearchCount);
    }

    newSearches.forEach((item, i) => {
        Cookie.createCookie(`RecentSearch${i}`, item);
    });
}

async function defaultSearch(context, query = null, pageSize = textSearchPageSize, sort) {
    return SearchContext.fromSearchApiResponse(
        context,
        await SearchApi.contentSearch(query, 1, pageSize, sort, context.facets)
    );
}

const defaultContext = (initialContext) => SearchContext.create(initialContext);

export default function HeaderSearch({
    className,
    id,
    style,
    onSearch = defaultSearch,
    linkSections,
    relatedSearchCount,
    theme = 'light',
    searchResultsPage,
}) {
    const [context, dispatch] = React.useReducer(SearchContext.reducer, defaultContext({}));
    const viewport = useViewport();
    const drawerRef = React.useRef(null);
    const headerSearchRef = React.useRef(null);
    const initialResultsRef = React.useRef([]);
    const [results, setResults] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [showInitialResults, setShowInitialResults] = React.useState(true);

    const setQuery = React.useCallback(
        (query) => dispatch({ type: 'setQuery', payload: query }),
        []
    );

    const search = React.useCallback(
        async (ctx, query, pageSize = textSearchPageSize, sort = 'Relevance') => {
            setIsLoading(true);
            try {
                const newCtx = await onSearch(ctx, query, pageSize, sort);
                
                if (!initialResultsRef.current.length) initialResultsRef.current = newCtx.results ?? [];
                else setShowInitialResults(false);
                dispatch({ type: 'setResults', payload: newCtx });
            } finally {
                setIsLoading(false);
            }
        },
        [onSearch]
    );

    const debounceSearch = React.useMemo(
        () => debounce((ctx, query) => search(ctx, query), 900),
        [search]
    );

    const onTextChange = React.useCallback(
        async (text) => {
            setQuery(text);
            if (text.length > 2) await debounceSearch(context, text);
            else debounceSearch.cancel();
        },
        [setQuery, debounceSearch, context]
    );

    const onClear = React.useCallback(() => {
        setQuery(null);
        HeaderController.closeSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const recentSearches = React.useMemo(() => {
        const recents = [];

        for (let i = 0; i < relatedSearchCount; i++) {
            const query = Cookie.getCookieByName(`RecentSearch${i}`);

            if (query) recents.push({ title: query, link: `${searchResultsPage}?query=${query}` });
        }
        return recents;
    }, [relatedSearchCount, searchResultsPage]);

    const onSubmit = React.useCallback(
        (e) => {
            e.preventDefault();
            updateRecents(recentSearches, relatedSearchCount, context.query);
            const url = new URL(searchResultsPage, window.location.origin);

            url.searchParams.set('query', context.query);
            window.location.href = url.toString();
        },
        [recentSearches, relatedSearchCount, context.query, searchResultsPage]
    );

    // Update results state whenever context or initialResults change
    React.useEffect(() => {
        setResults(showInitialResults ? initialResultsRef.current : context.results ?? []);
    }, [showInitialResults, context.results]);

    const searchDrawerLinks = React.useMemo(() => {
        if (!showInitialResults && (linkSections?.length || recentSearches?.length)) {
            return <SearchDrawerLinks className={`theme-${theme}`} recentSearches={recentSearches} linkSections={linkSections} />;
        }
        return null;
    }, [showInitialResults, linkSections, recentSearches, theme]);

    const resultsLink = React.useMemo(() => {
        if (!showInitialResults) {
            return (
                <a href={`${searchResultsPage}?query=${context.query}`}>
                    <Translation id="Search.Drawer.View.All.Link.Text" params={{ count: context.pageInfo.total }} />
                </a>
            );
        }
        return null;
    }, [showInitialResults, searchResultsPage, context.query, context.pageInfo.total]);

    const handleClickOutsideSearch = React.useCallback(
        (e) => {
            const element = document.getElementById(id);

            if (element && !element.contains(e.target)) HeaderController.closeSearch();
        },
        [id]
    );

    React.useEffect(() => {
        headerSearchRef.current = document.getElementById(id);
        const el = headerSearchRef.current;

        async function initialSearch() {
            setShowInitialResults(true);
            setTimeout(() => document.addEventListener('click', handleClickOutsideSearch), 300);
            if (!initialResultsRef.current.length) await search(context, null, initialPageSize, 'BestSeller');
        }

        if (el) {
            el.addEventListener('open.search', initialSearch);
            return () => el.removeEventListener('open.search', initialSearch);
        }
    }, [context, handleClickOutsideSearch, search, id]);

    React.useEffect(() => {
        const el = headerSearchRef.current;

        if (!el) return;

        const onClose = () => document.removeEventListener('click', handleClickOutsideSearch);

        el.addEventListener('close.search', onClose);
        return () => el.removeEventListener('close.search', onClose);
    }, [handleClickOutsideSearch]);

    const isMobile = viewport.is.lt('md');

    return (
        <div id={id} ref={headerSearchRef} style={style} className={cx(className, 'HeaderSearch', `theme-${theme}`)}>
            <SearchBar
                className={cx(`theme-${theme}`, 'container')}
                showLoader={isLoading}
                onSubmit={onSubmit}
                onTextChange={onTextChange}
                onClear={onClear}
                query={context.query}
            />
            <div
                ref={drawerRef}
                style={{ height: results?.length || resultsLink || searchDrawerLinks ? 'auto' : '0px' }}
                className={cx('HeaderSearch__drawer', `theme-${theme}`, 'justify-content-center')}
            >
                <Section
                    sidebarLeft={isMobile ? null : searchDrawerLinks}
                    sidebarRight={isMobile ? searchDrawerLinks : null}
                    className="container"
                >
                    {resultsLink && <div className="HeaderSearch__drawer--link">{resultsLink}</div>}
                    {!results?.length && initialResultsRef.current?.length ? (
                        <Translation id="Search.No.Results.Message" />
                    ) : (
                        <ResultsGrid gap={isMobile ? 1 : 4}>
                            {results?.map((result) => (
                                <HeaderSearchResult
                                    className={`theme-${theme}`}
                                    result={result}
                                    key={result.product?.url}
                                />
                            ))}
                        </ResultsGrid>
                    )}
                </Section>
            </div>
        </div>
    );
}

HeaderSearch.propTypes = {
    style: px.object,
    className: px.string,
    onSearch: px.func,
    linkSections: px.arrayOf(
        px.shape({
            Title: px.string,
            Links: px.arrayOf(
                px.shape({
                    Href: px.string,
                    Title: px.string,
                })
            ),
        })
    ),
    relatedSearchCount: px.number,
    id: px.string,
    theme: px.string,
    searchResultsPage: px.string,
};
